import { useCallback, useEffect, useMemo, useState } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import ListSubheader from "@mui/material/ListSubheader";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";
import VuiSwitch from "components/VuiSwitch";
import { useAuthz, ROLES } from "context/AuthzContext";
import { useCompany } from "context/CompanyContext";
import { createTenantUser, createTenantUserInvite, listTenantRoles } from "services/tenantUserAdminService";
import PropTypes from "prop-types";
import { TC } from "../tokens/text";

// Custom roles are keyed as "custom:<roleId>"; base roles use their plain string value.
function parseRoleKey(key) {
  if (key && key.startsWith("custom:")) {
    return { baseRole: ROLES.USER, customRoleIds: [key.slice(7)] };
  }
  return { baseRole: key || ROLES.USER, customRoleIds: [] };
}

function normalizeErrorMessage(err) {
  const message = err?.message || "Failed to create user.";
  if (message.includes("permission-denied")) return "You do not have permission to create users for this tenant.";
  if (message.includes("already-exists") || message.includes("email-already-exists")) {
    return "A user with this email already exists.";
  }
  return message;
}

const SELECT_SX = {
  color: TC.primary,
  background: "#f4f8ff",
  borderRadius: "12px",
  "& .MuiSvgIcon-root": { color: TC.primary },
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
};

const PLACEHOLDER = (text) => (
  <span style={{ color: TC.primary, fontSize: "12px", opacity: 0.6 }}>{text}</span>
);

const MENU_PROPS = {
  PaperProps: {
    sx: {
      backgroundColor: "#ffffff",
      color: TC.primary,
      "& .MuiMenuItem-root": { color: TC.primary },
      "& .MuiMenuItem-root:hover": { backgroundcolor: "#7a94b0" },
      "& .MuiMenuItem-root.Mui-selected": { backgroundColor: "rgba(0,117,255,0.18)" },
      "& .MuiListSubheader-root": {
        backgroundColor: "transparent",
        color: TC.secondary,
        fontSize: "11px",
        lineHeight: "28px",
      },
    },
  },
};

function TenantUserCreateForm({ onUserChanged }) {
  const { role: currentRole, isSuperAdmin, tenantId: currentTenantId } = useAuthz();
  const { companies, selectedCompanyId } = useCompany();

  const [selectedTenantId, setSelectedTenantId] = useState("");
  const activeTenantId = isSuperAdmin ? selectedTenantId || "" : currentTenantId || "";

  // Default the tenant selector to the first company when companies load
  useEffect(() => {
    if (isSuperAdmin && !selectedTenantId && Array.isArray(companies) && companies.length > 0) {
      setSelectedTenantId(companies[0].id);
    }
  }, [isSuperAdmin, selectedTenantId, companies]);

  const [form, setForm] = useState({ email: "", displayName: "", password: "" });
  const [selectedRoleKey, setSelectedRoleKey] = useState(ROLES.USER);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inviteMode, setInviteMode] = useState(true);
  const [isTenantUser, setIsTenantUser] = useState(false);
  const [customRoles, setCustomRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [error, setError] = useState("");
  const [createdUser, setCreatedUser] = useState(null);

  const canCreateUsers = useMemo(
    () => currentRole === ROLES.SUPER_ADMIN || currentRole === ROLES.TENANT_ADMIN,
    [currentRole]
  );

  // Super admin can assign tenant_admin and supervisor; tenant admin can only create regular users.
  const creatableRoles = useMemo(() => {
    if (currentRole === ROLES.SUPER_ADMIN) return [ROLES.USER, ROLES.SUPERVISOR, ROLES.TENANT_ADMIN];
    if (currentRole === ROLES.TENANT_ADMIN) return [ROLES.USER, ROLES.SUPERVISOR];
    return [ROLES.USER];
  }, [currentRole]);

  const loadCustomRoles = useCallback(async () => {
    if (!activeTenantId) { setCustomRoles([]); return; }
    setRolesLoading(true);
    try {
      const result = await listTenantRoles({ tenantId: activeTenantId });
      setCustomRoles(Array.isArray(result?.roles) ? result.roles : []);
    } catch {
      setCustomRoles([]);
    } finally {
      setRolesLoading(false);
    }
  }, [activeTenantId]);

  useEffect(() => { loadCustomRoles(); }, [loadCustomRoles]);

  // When "Tenant User" toggle changes, lock/unlock role selection (super admin only feature).
  useEffect(() => {
    if (isTenantUser) {
      setSelectedRoleKey(ROLES.TENANT_ADMIN);
    } else {
      setSelectedRoleKey(ROLES.USER);
    }
  }, [isTenantUser]);

  const handleChange = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canCreateUsers || isSubmitting) return;

    if (!activeTenantId) {
      setError("Select a tenant scope before creating users.");
      return;
    }

    setIsSubmitting(true);
    setError("");
    setCreatedUser(null);

    try {
      const { baseRole, customRoleIds } = parseRoleKey(selectedRoleKey);
      // Ensure tenant_admin role is only assignable by super_admin.
      const safeRole = baseRole === ROLES.TENANT_ADMIN && currentRole !== ROLES.SUPER_ADMIN ? ROLES.USER : baseRole;
      const payload = {
        email: form.email.trim(),
        displayName: form.displayName.trim(),
        role: safeRole,
        tenantId: activeTenantId,
        ...(customRoleIds.length > 0 && { customRoleIds }),
      };

      if (!inviteMode) payload.password = form.password;

      const result = inviteMode ? await createTenantUserInvite(payload) : await createTenantUser(payload);
      setCreatedUser(result);
      setForm({ email: "", displayName: "", password: "" });
      if (onUserChanged) onUserChanged();
    } catch (submitError) {
      setError(normalizeErrorMessage(submitError));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!canCreateUsers) {
    return (
      <Card>
        <VuiBox p={3}>
          <VuiTypography color="warning" variant="button">
            Only admins can create users.
          </VuiTypography>
        </VuiBox>
      </Card>
    );
  }

  const selectedLabel = (() => {
    if (selectedRoleKey === ROLES.TENANT_ADMIN) return "Tenant Admin";
    if (selectedRoleKey === ROLES.SUPERVISOR)   return "Supervisor";
    if (selectedRoleKey === ROLES.USER)         return "User";
    const found = customRoles.find((r) => `custom:${r.roleId || r.id}` === selectedRoleKey);
    return found?.name || selectedRoleKey;
  })();

  return (
    <Card>
      <VuiBox p={3} component="form" onSubmit={handleSubmit}>
        <VuiTypography variant="lg" mb={2} sx={{ color: TC.primary }}>
          Create User
        </VuiTypography>

        <VuiBox display="flex" alignItems="center" mb={2}>
          <VuiSwitch color="info" checked={inviteMode} onChange={() => setInviteMode((prev) => !prev)} />
          <VuiTypography variant="button" ml={1} sx={{ color: TC.primary }}>
            Invite mode (send password reset link)
          </VuiTypography>
        </VuiBox>

        {isSuperAdmin && (
          <VuiBox display="flex" alignItems="center" mb={2}>
            <VuiSwitch
              color="warning"
              checked={isTenantUser}
              onChange={() => setIsTenantUser((prev) => !prev)}
            />
            <VuiTypography variant="button" ml={1} sx={{ color: TC.primary }}>
              Enable as Tenant User{isTenantUser ? " — role set to Tenant Admin" : ""}
            </VuiTypography>
          </VuiBox>
        )}

        {error ? (
          <VuiTypography color="error" variant="button" mb={2} display="block">{error}</VuiTypography>
        ) : null}

        {createdUser ? (
          <VuiTypography color="success" variant="button" mb={2} display="block">
            User created: {createdUser.email} ({createdUser.role}
            {createdUser.assignedRoleIds?.length ? ` + ${createdUser.assignedRoleIds.join(", ")}` : ""})
            {createdUser.resetLink ? ` | Reset link: ${createdUser.resetLink}` : ""}
          </VuiTypography>
        ) : null}

        <Grid container spacing={2}>
          {isSuperAdmin && (
            <Grid item xs={12} md={6}>
              <Select
                value={selectedTenantId}
                onChange={(e) => setSelectedTenantId(e.target.value)}
                aria-label="Select tenant"
                fullWidth
                size="small"
                displayEmpty
                renderValue={(val) => {
                  if (!val) return PLACEHOLDER("Select a tenant");
                  const found = Array.isArray(companies) ? companies.find((c) => c.id === val) : null;
                  return found?.name || val;
                }}
                MenuProps={MENU_PROPS}
                sx={SELECT_SX}
              >
                {(companies || []).map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name || c.id}</MenuItem>
                ))}
              </Select>
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <VuiInput placeholder="Email" value={form.email} onChange={handleChange("email")} aria-label="Email" required />
          </Grid>
          <Grid item xs={12} md={6}>
            <VuiInput placeholder="Display Name" value={form.displayName} onChange={handleChange("displayName")} aria-label="Display name" required />
          </Grid>
          <Grid item xs={12} md={6}>
            <VuiInput
              type="password"
              placeholder={inviteMode ? "Optional (not used in invite mode)" : "Temporary Password"}
              value={form.password}
              onChange={handleChange("password")}
              aria-label="Password"
              required={!inviteMode}
              disabled={inviteMode}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Select
              value={selectedRoleKey}
              onChange={(e) => setSelectedRoleKey(e.target.value)}
              aria-label="Select role"
              disabled={isTenantUser}
              fullWidth
              size="small"
              renderValue={() => selectedLabel || PLACEHOLDER(rolesLoading ? "Loading roles..." : "Select a role")}
              MenuProps={MENU_PROPS}
              sx={SELECT_SX}
            >
              <ListSubheader>Base Roles</ListSubheader>
              {creatableRoles.map((r) => (
                <MenuItem key={r} value={r}>
                  {r === ROLES.TENANT_ADMIN ? "Tenant Admin" : r === ROLES.SUPERVISOR ? "Supervisor" : "User"}
                </MenuItem>
              ))}
              {customRoles.length > 0 && <ListSubheader>Custom Roles</ListSubheader>}
              {customRoles.map((r) => {
                const key = `custom:${r.roleId || r.id}`;
                return (
                  <MenuItem key={key} value={key}>
                    {r.name || r.roleId || r.id}
                  </MenuItem>
                );
              })}
            </Select>
          </Grid>
        </Grid>

        <VuiBox mt={3} display="flex" justifyContent="flex-end">
          <VuiButton color="info" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Processing..." : inviteMode ? "Create & Invite" : "Create User"}
          </VuiButton>
        </VuiBox>
      </VuiBox>
    </Card>
  );
}

TenantUserCreateForm.defaultProps = {
  onUserChanged: null,
};

TenantUserCreateForm.propTypes = {
  onUserChanged: PropTypes.func,
};

export default TenantUserCreateForm;

